import React, { FunctionComponent, useEffect, useRef } from 'react';
import { MathUtils, PerspectiveCamera } from 'three';
import DragControls, { IDragLookHandle } from '@r3f/DragControls.tsx/DragControls';
import { useSwitchDefaultCameras } from './TrackerGroups/Content360Wrapper/content360Hooks';
import { useGetCurrentSceneId } from '../hooks/general';
import { useDataStore } from '../zustand';
import { IContent360Scene, ITuple3 } from '@r3f-component-data-structure';
import { Jute } from '..';

interface IParentProps {
    enabled: boolean;
    camera: PerspectiveCamera;
    nonDragCamera?: PerspectiveCamera;
    disableVerticalDrag?: boolean;
}

const DragControlsWrapper: FunctionComponent<IParentProps> = ({ enabled, camera: dragCamera, nonDragCamera, disableVerticalDrag }) => {
    const currentSceneId = useGetCurrentSceneId();
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const currentScene = componentsById?.[currentSceneId] as IContent360Scene | undefined;
    const lookRef = useRef<IDragLookHandle>(null);
    const initialRotation = currentScene?.content360?.initialCameraRotation ? [...currentScene?.content360!.initialCameraRotation] : undefined;
    if (initialRotation) {
        initialRotation[1] = initialRotation[1] + 180;
        initialRotation[0] = initialRotation[0];
    }

    useSwitchDefaultCameras(dragCamera, nonDragCamera);

    // The preview simulator's keys reach us from another document, so they arrive through Jute rather
    // than as events. Registered only while a 360 scene is the enabled one: this wrapper is mounted for
    // the whole session whenever the project has any 360 scene at all.
    //
    // Azimuth is negated because the two APIs describe opposite things by the same sign — Jute's
    // positive azimuth swings a camera LEFT around its target, and looking left is `lon` decreasing.
    useEffect(() => {
        if (!Jute.isPreviewSimulator || !enabled) return;
        Jute.registerPreviewLookControls({
            look: (deltaAzimuth, deltaPolar) =>
                lookRef.current?.look(-MathUtils.radToDeg(deltaAzimuth), MathUtils.radToDeg(deltaPolar))
        });
        return () => Jute.registerPreviewLookControls(undefined);
    }, [enabled]);

    return <DragControls enabled={enabled} camera={dragCamera} zoomEnabled={false} initialRotation={initialRotation as ITuple3} verticalMovementFactor={-1} disableVerticalDrag={disableVerticalDrag} enableCameraResetOnSceneLoad lookHandle={lookRef}/>
}

DragControlsWrapper.displayName = 'dragControlsWrapper';
export default DragControlsWrapper;
